import { useQueryClient } from "@tanstack/react-query";
import { PhoneCall } from "lucide-react";
import { useState } from "react";
import { QuickLog } from "@/components/CallLog";
import { TextButton } from "@/components/TextButton";
import type { CallOwner } from "@/lib/callLog";
import { formatPhone } from "@/lib/format";
import { cn } from "@/lib/utils";
import { recordDial, VOICE_WINDOW, voiceDialUrl } from "@/lib/voice";

/**
 * v2.10 P1.2 / P3.3 / P4.2: Dial and Text, side by side — on the lead page, the deal page and the drawer.
 * The big button opens Google Voice's own dialog in hello@'s account (Sam presses Call there; nothing here
 * dials, D-133); Text opens the Text dialog (Sam's Send, D-132). `tel:` lives only in each number's ⋯.
 */
export function DialButton({
  phone,
  owner,
  surface,
  className,
}: {
  phone: string;
  owner: CallOwner;
  surface: string;
  className?: string;
}) {
  return (
    <a
      href={voiceDialUrl(phone)}
      target={VOICE_WINDOW}
      data-testid="dial-button"
      data-dial="voice"
      title="Opens Google Voice's dialer for this number (your 931 line) — you press Call there"
      className={cn(
        "inline-flex min-h-9 items-center gap-2 rounded-md border border-brand bg-brand px-3 text-sm font-semibold text-brand-fg hover:opacity-90 pointer-coarse:min-h-11",
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(phone).catch(() => undefined);
        recordDial(phone, owner.kind === "lead" ? { leadId: owner.id, surface } : { dealId: owner.id, surface });
      }}
    >
      <PhoneCall className="size-4" aria-hidden />
      <span>Call {formatPhone(phone)}</span>
    </a>
  );
}

export function DialBar({
  owner,
  phones,
  surface,
  className,
}: {
  owner: CallOwner;
  phones: readonly string[];
  surface: string;
  className?: string;
}) {
  const [phone, setPhone] = useState<string>(phones[0] ?? "");
  const chosen = phones.includes(phone) ? phone : (phones[0] ?? "");
  if (phones.length === 0) {
    return <p className={cn("text-xs text-fg-muted", className)}>No phone on file to call or text.</p>;
  }
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)} data-testid="dial-bar">
      <DialButton phone={chosen} owner={owner} surface={surface} />
      <TextButton owner={owner} phones={phones} phone={chosen} testId="text-button" />
      {phones.length > 1 && (
        <select
          value={chosen}
          onChange={(e) => setPhone(e.target.value)}
          aria-label="Which number"
          data-testid="dial-bar-phone"
          className="min-h-9 rounded-md border bg-surface px-2 text-sm pointer-coarse:min-h-11 pointer-coarse:text-base"
        >
          {phones.map((p) => (
            <option key={p} value={p}>
              {formatPhone(p)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * P4.2: on a phone, the lead / deal page keeps Dial, Text and the quick-log chips in a sticky bottom bar,
 * above the tab bar. Hidden from 768 px up, where the same controls sit in the page.
 */
export function MobileCallBar({ owner, phones }: { owner: CallOwner; phones: readonly string[] }) {
  const qc = useQueryClient();
  if (phones.length === 0) return null;
  return (
    <div
      className="fixed inset-x-0 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-30 space-y-1.5 border-t bg-surface/95 px-3 py-2 backdrop-blur md:hidden"
      data-testid="mobile-call-bar"
    >
      <DialBar owner={owner} phones={phones} surface="mobile-bar" />
      <QuickLog
        owner={owner}
        phones={phones}
        compact
        testId="mobile-quicklog"
        onLogged={() => void qc.invalidateQueries()}
      />
    </div>
  );
}
